// getSubcategoryStatus.js

import { calculateMonthlyBudget } from "./calculateMonthlyBudget";

// 🔑 Add these helpers at the top
function getDaysUntilReset(frequency, today) {
  const now = today || new Date();
  let nextReset;

  if (frequency === "weekly") {
    const daysAhead = (7 - now.getDay()) % 7;
    nextReset = new Date(now);
    nextReset.setDate(now.getDate() + daysAhead);
  } else if (frequency === "bi-weekly") {
    const anchor = new Date("2025-06-30"); // Example anchor
    nextReset = new Date(anchor);
    while (nextReset <= now) {
      nextReset.setDate(nextReset.getDate() + 14);
    }
  } else if (frequency === "twice-monthly") {
    if (now.getDate() <= 15) {
      nextReset = new Date(now.getFullYear(), now.getMonth(), 15);
    } else {
      nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    }
  } else if (frequency === "quarterly") {
    const month = now.getMonth();
    if (month <= 2) nextReset = new Date(now.getFullYear(), 3, 1);
    else if (month <= 5) nextReset = new Date(now.getFullYear(), 6, 1);
    else if (month <= 8) nextReset = new Date(now.getFullYear(), 9, 1);
    else nextReset = new Date(now.getFullYear() + 1, 0, 1);
  } else if (frequency === "annual") {
    nextReset = new Date(now.getFullYear() + 1, 0, 1);
  } else {
    nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  const diff = Math.round((nextReset - now) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : null;
}

function getResetDate(frequency, today) {
  const now = today || new Date();
  let nextReset;

  if (frequency === "weekly") {
    const daysAhead = (7 - now.getDay()) % 7;
    nextReset = new Date(now);
    nextReset.setDate(now.getDate() + daysAhead);
  } else if (frequency === "bi-weekly") {
    const anchor = new Date("2025-06-30"); // Example anchor
    nextReset = new Date(anchor);
    while (nextReset <= now) {
      nextReset.setDate(nextReset.getDate() + 14);
    }
  } else if (frequency === "twice-monthly") {
    if (now.getDate() <= 15) {
      nextReset = new Date(now.getFullYear(), now.getMonth(), 15);
    } else {
      nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    }
  } else if (frequency === "quarterly") {
    const month = now.getMonth();
    if (month <= 2) nextReset = new Date(now.getFullYear(), 3, 1);
    else if (month <= 5) nextReset = new Date(now.getFullYear(), 6, 1);
    else if (month <= 8) nextReset = new Date(now.getFullYear(), 9, 1);
    else nextReset = new Date(now.getFullYear() + 1, 0, 1);
  } else if (frequency === "annual") {
    nextReset = new Date(now.getFullYear() + 1, 0, 1);
  } else {
    nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  }

  return nextReset.toLocaleDateString();
}

function getNextPayDate(startDate, frequency, today = new Date()) {
  if (!startDate || !frequency) return "";
  let cursor = new Date(startDate);

  const interval = frequency === "weekly"
    ? 7
    : frequency === "bi-weekly"
    ? 14
    : frequency === "quarterly"
    ? 91
    : frequency === "annual"
    ? 365
    : null;

  if (!interval) return startDate;

  while (cursor <= today) {
    cursor.setDate(cursor.getDate() + interval);
  }

  return cursor.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

// 🔑 Main export
export function getSubcategoryStatus({ subcategory, actual, isIncome = false, today = new Date() }) {
  const { frequency, normalize, payDate, customAmounts, amount, trackPacing = true } = subcategory;

  const monthlyBudgetStr = calculateMonthlyBudget(subcategory, today);
  const monthlyBudget = parseFloat(monthlyBudgetStr.replace(/[^\d.-]/g, ""));

  const year = today.getFullYear();
  const month = today.getMonth();
  const startOfMonth = new Date(year, month, 1);
  const endOfMonth = new Date(year, month + 1, 0);
  const daysInMonth = endOfMonth.getDate();
  const dayOfMonth = today.getDate();

  let expectedToDate = 0;

  const countResetsPassed = (resetIntervalDays) => {
    if (!payDate) return 0;
    const payStart = new Date(payDate);
    let count = 0;
    let date = new Date(payStart);
    while (date <= today) {
      if (date >= startOfMonth && date <= today) count++;
      date.setDate(date.getDate() + resetIntervalDays);
    }
    return count;
  };

  if (!trackPacing) {
    expectedToDate = monthlyBudget;
  } else if (frequency === "custom") {
    const customAmount = parseFloat(customAmounts?.[month] || 0);
    const percentElapsed = dayOfMonth / daysInMonth;
    expectedToDate = customAmount * percentElapsed;
  } else if (frequency === "twice-monthly") {
    const percentElapsed = dayOfMonth / daysInMonth;
    expectedToDate = monthlyBudget * percentElapsed;
  } else if (!normalize && payDate) {
    let periodLength = 30;
    switch (frequency) {
      case "weekly": periodLength = 7; break;
      case "bi-weekly": periodLength = 14; break;
      case "quarterly": periodLength = 91; break;
      case "annual": periodLength = 365; break;
      default: periodLength = 30;
    }
    const resetsPassed = countResetsPassed(periodLength);
    expectedToDate = (parseFloat(amount) || 0) * resetsPassed;
  } else {
    const percentElapsed = dayOfMonth / daysInMonth;
    expectedToDate = monthlyBudget * percentElapsed;
  }

  let emoji = "🟢";
  let label = "On Track";
  let statusMessage = "";

  if (isIncome) {
    const diff = expectedToDate - actual;
    const percentBehind = diff / expectedToDate;

    statusMessage = `Received $${actual.toLocaleString(undefined, { minimumFractionDigits: 2 })} so far. Expected: $${expectedToDate.toLocaleString(undefined, { minimumFractionDigits: 2 })}.`;

    if (actual >= monthlyBudget) {
        emoji = "🟢";
        label = "Goal Met";
    } else if (diff > 0 && percentBehind > 0.30) {
        emoji = "🔴";
        label = "Behind";
    } else if (diff > 0 && percentBehind > 0.05) {
        emoji = "🟡";
        label = "Caution";
    } else {
        emoji = "🟢";
        label = "On Track";
    }
    }

  else {
  const diff = actual - expectedToDate;
  const percentOverExpected = diff / expectedToDate;

  statusMessage = `Spent $${actual.toLocaleString(undefined, { minimumFractionDigits: 2 })} so far. Expected: $${expectedToDate.toLocaleString(undefined, { minimumFractionDigits: 2 })}.`;

  if (actual > monthlyBudget) {
    emoji = "🔴";
    label = "Over Budget";
  } else if (diff > 0 && percentOverExpected > 0.30) {
    emoji = "🔴";
    label = "Over Pace";
  } else if (diff > 0 && percentOverExpected > 0.05 && actual > 0.9 * monthlyBudget) {
    emoji = "🟡";
    label = "Caution";
  } else {
    emoji = "🟢";
    label = "On Track";
  }
}

let nextMonthDiffNote = "";
if (frequency === "custom") {
  const nextMonth = (month + 1) % 12;
  const nextCustom = parseFloat(customAmounts?.[nextMonth] || 0);
  const diff = nextCustom - monthlyBudget;
  const diffText = diff >= 0 ? `+$${diff.toFixed(2)}` : `-$${Math.abs(diff).toFixed(2)}`;
  nextMonthDiffNote = `Custom budget next month: $${nextCustom.toFixed(2)} (${diffText} from this month)`;
}

  const payDatePassed = payDate && !trackPacing && new Date(payDate) < today;

  return {
    emoji,
    label,
    statusMessage,
    meta: {
      expectedToDate,
      actual,
      monthlyBudget,
      expectedForRollup: trackPacing ? expectedToDate : monthlyBudget,
      nextPayDate:
        payDate &&
        !normalize &&
        trackPacing &&
        ["weekly", "bi-weekly", "quarterly", "annual"].includes(frequency)
            ? getNextPayDate(payDate, frequency, today)
            : null,
      payDatePassed,
      daysUntilReset: getDaysUntilReset(frequency, today),
      nextReset: getResetDate(frequency, today),
      nextMonthDiffNote: frequency === "custom" ? nextMonthDiffNote : "",
    }
  };
}

// 🔹 Rollup-only status helper — uses already-aggregated numbers.
//    expectedToDate: sum of pacing expectations for all children (what you already compute as meta.expectedForRollup).
//    actual:         sum of actual MTD for the rollup.
//    monthlyBudget:  total monthly budget for the rollup.
//    isPositiveGood: true for Income/Savings (more is good), false for Expenses (less is good).
export function getRollupStatus({ expectedToDate, actual, monthlyBudget, isPositiveGood }) {
  // Guardrails against NaN/0 and division by zero
  const safeExpected = Number.isFinite(expectedToDate) ? expectedToDate : 0;
  const safeActual = Number.isFinite(actual) ? actual : 0;
  const safeMonthly = Number.isFinite(monthlyBudget) ? monthlyBudget : 0;

  let emoji = "🟢";
  let label = "On Track";
  let statusMessage = "";

  if (isPositiveGood) {
    // Income-like logic (matches getSubcategoryStatus thresholds)
    const diff = safeExpected - safeActual;
    const percentBehind = safeExpected > 0 ? diff / safeExpected : 0;

    statusMessage =
      `Received/Saved $${safeActual.toLocaleString(undefined, { minimumFractionDigits: 2 })} so far. ` +
      `Expected: $${safeExpected.toLocaleString(undefined, { minimumFractionDigits: 2 })}.`;

    if (safeActual >= safeMonthly) {
      emoji = "🟢";
      label = "Goal Met";
    } else if (diff > 0 && percentBehind > 0.30) {
      emoji = "🔴";
      label = "Behind";
    } else if (diff > 0 && percentBehind > 0.05) {
      emoji = "🟡";
      label = "Caution";
    } else {
      emoji = "🟢";
      label = "On Track";
    }
  } else {
    // Expense-like logic (matches getSubcategoryStatus thresholds)
    const diff = safeActual - safeExpected;
    const percentOverExpected = safeExpected > 0 ? diff / safeExpected : 0;

    statusMessage =
      `Spent $${safeActual.toLocaleString(undefined, { minimumFractionDigits: 2 })} so far. ` +
      `Expected: $${safeExpected.toLocaleString(undefined, { minimumFractionDigits: 2 })}.`;

    if (safeActual > safeMonthly) {
      emoji = "🔴";
      label = "Over Budget";
    } else if (diff > 0 && percentOverExpected > 0.30) {
      emoji = "🔴";
      label = "Over Pace";
    } else if (diff > 0 && percentOverExpected > 0.05 && safeActual > 0.9 * safeMonthly) {
      emoji = "🟡";
      label = "Caution";
    } else {
      emoji = "🟢";
      label = "On Track";
    }
  }

  return {
    emoji,
    label,
    statusMessage,
    meta: {
      expectedToDate: safeExpected,
      actual: safeActual,
      monthlyBudget: safeMonthly
    }
  };
}
